// File: ViewSuperAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Label,
  Row,
  CardBody,
  Alert,
  Spinner,
} from "reactstrap";
import {
  SubscriptinPlanList,
  View_User_By_Id,
} from "../../import/ApiEndPoint/Api";
import {
  _Get,
  _GetList,
  _Put,
  ViewCompanyDetails,
  DeleteAccount,
} from "../../import/ApiEndPoint/ApiCalling";
import swal from "sweetalert";
import { useNavigate, useParams } from "react-router-dom";
import { baseURL } from "../../import/ApiEndPoint/TestApi";

/* page-scoped CSS */
const injectOnce = () => {
  if (document.getElementById("vs-inline-css")) return;
  const s = document.createElement("style");
  s.id = "vs-inline-css";
  s.textContent = `
    .vs-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .vs-toolbar{ padding:8px; background:#f4f7f6; border-radius:14px; border:1px solid #e5e7eb; }
    .vs-title{ font-weight:800; text-transform:uppercase; font-size:18px; }
    /* NEW: tidy spacing between the big sections */
    .vs-sections{
      display:flex;
      flex-wrap:wrap;
      gap:16px;                 /* <— consistent space between sections on all screens */
      justify-content:flex-start;
      align-items:flex-start;
    }
    /* Optional tiny bottom margin so stacked layout breathes too */
    .vs-section{ margin-bottom:8px; }
  `;
  document.head.appendChild(s);
};

function ViewSuperAdmin() {
  const [Data, setData] = useState({});
  const [SubsCriptionList, setSubsCriptionList] = useState([]);
  const [companyDetails, setCompanyDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const navigate = useNavigate();
  const Params = useParams();

  useEffect(() => injectOnce(), []);

  // Resolve database robustly (don’t block if missing)
  const db = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      return (
        Data?.database ||
        u?.database ||
        u?.db ||
        u?.DBName ||
        u?.dbname ||
        u?.Database ||
        ""
      );
    } catch {
      return Data?.database || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Data?.database]);

  // ===== LOAD: User (try with db, then without) + Plans =====
  useEffect(() => {
    (async () => {
      if (!Params?.id) return;
      setLoading(true);
      setErrMsg("");

      try {
        let res;
        // 1) Try /:id/:db when db exists
        if (db) {
          try {
            res = await _Get(
              View_User_By_Id,
              `${Params.id}/${encodeURIComponent(db)}`
            );
          } catch (e1) {
            // fallback below
          }
        }
        // 2) Fallback: /:id only
        if (!res) {
          res = await _Get(View_User_By_Id, Params.id);
        }

        const value = res?.User;
        if (!value) {
          setErrMsg("No user found for this id.");
          setData({});
        } else {
          const a = value?.subscriptionPlan || {};
          const merged = {
            ...value,
            annualMaintenanceCost: a?.annualMaintenanceCost,
            createdAt: a?.createdAt,
            days: a?.days,
            noOfUser: a?.noOfUser,
            planName: a?.planName,
            subscriptionPlan: a?._id,
            subscriptionCost: a?.subscriptionCost,
            subscriptionType: a?.subscriptionType,
          };
          setData(merged);
        }

        // Plans — prefer db if we have it
        try {
          const plans = await _GetList(SubscriptinPlanList, db || undefined);
          const list = Array.isArray(plans?.Subscription)
            ? plans.Subscription
            : [];
          setSubsCriptionList(list);
        } catch {
          setSubsCriptionList([]);
        }
      } catch (err) {
        setErrMsg(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load user details"
        );
        setData({});
      } finally {
        setLoading(false);
      }
    })();
  }, [Params?.id, db]);

  // ===== COMPANY DETAILS =====
  useEffect(() => {
    (async () => {
      try {
        if (!Params?.id) return;
        const response = await ViewCompanyDetails(Params.id, db || undefined);
        setCompanyDetails(response?.CompanyDetail || {});
      } catch {
        setCompanyDetails({});
      }
    })();
  }, [Params?.id, db]);

  // ===== FORM HANDLERS =====
  const handleInputChange = (e) => {
    const { value, name } = e.target;
    setData((p) => ({ ...p, [name]: value }));
  };

  // ===== ACTIONS =====
  const handleEdit = () => {
    navigate(`/app/master/superadmins/edit/${Params?.id}`);
  };

  const handleDelete = () => {
    swal("Warning", "Sure you want to delete this Super Admin?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((v) => {
      if (v === "delete") {
        DeleteAccount(Params?.id)
          .then(() => {
            swal("Deleted", "Record deleted successfully", "success");
            navigate(-1);
          })
          .catch(() => {
            swal("Error", "Delete failed", "error");
          });
      }
    });
  };

  if (loading) {
    return (
      <>
        <Card className="vs-toolbar mb-1">
          <div className="d-flex justify-content-between align-items-center">
            <div className="vs-title">View Super Admin</div>
            <div className="d-flex" style={{ gap: 8 }}>
              <Button color="secondary" onClick={() => navigate(-1)}>
                Back
              </Button>
            </div>
          </div>
        </Card>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "6rem",
          }}
        >
          <Spinner style={{ height: "3rem", width: "3rem" }} color="primary">
            Loading...
          </Spinner>
        </div>
      </>
    );
  }

  return (
    <>
      {/* ===== Title / Actions card ===== */}
      <Card className="vs-toolbar mb-1">
        <div className="d-flex justify-content-between align-items-center">
          <div className="vs-title">View Super Admin</div>
          <div className="d-flex" style={{ gap: 8 }}>
            <Button color="primary" onClick={handleEdit}>
              Edit
            </Button>
            <Button color="danger" onClick={handleDelete}>
              Delete
            </Button>
            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </div>
      </Card>

      {errMsg ? (
        <Alert color="danger" className="mb-2" style={{ borderRadius: 12 }}>
          {errMsg}
        </Alert>
      ) : null}

      {/* ===== Data card ===== */}
      <Card className="vs-card">
        <Form className="mb-2">
          <div className="vs-sections">
            {/* ===== Personal Details ===== */}
            <CardBody className="vs-section userRegiBody col-12 col-md-6">
              <h5>Personal Details</h5>
              <Row>
                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>FirstName*</Label>
                  <Input
                    type="text"
                    name="FirstName"
                    placeholder="Enter FirstName"
                    value={Data.firstName || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={Data.email || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Mobile Number *</Label>
                  <Input
                    type="number"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={Data.mobileNumber || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Pin Code *</Label>
                  <Input
                    type="text"
                    name="pincode"
                    placeholder="Pin Code"
                    value={Data.pincode || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Aadhar No.*</Label>
                  <Input
                    type="number"
                    name="Aadhar_No"
                    placeholder="Aadhar No"
                    value={Data.Aadhar_No || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>City *</Label>
                  <Input
                    type="text"
                    name="City"
                    placeholder="City"
                    value={Data.City || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>State *</Label>
                  <Input
                    type="text"
                    name="state"
                    placeholder="State"
                    value={Data.State || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Country *</Label>
                  <Input
                    type="text"
                    name="Country"
                    placeholder="Country"
                    value={Data.Country || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Pan No.*</Label>
                  <Input
                    type="text"
                    name="Pan_No"
                    placeholder="Pan No"
                    value={Data.Pan_No || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="12" md="12">
                  <Label>Personal Address One*</Label>
                  <Input
                    type="textarea"
                    name="addressOne"
                    placeholder="Address One"
                    value={Data.address1 || ""}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            </CardBody>

            {/* ===== Company Details ===== */}
            <CardBody className="vs-section userRegiBody col-12 col-md-5">
              <h5>Company Details</h5>
              <Row>
                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Company Name*</Label>
                  <Input
                    type="text"
                    name="companyName"
                    placeholder="Enter CompanyName"
                    value={companyDetails.name || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col
                  className="mb-1 mt-1"
                  lg="4"
                  md="4"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Label>
                    Signature{" "}
                    {companyDetails?.signature ? (
                      <img
                        src={`${baseURL}/Images/${companyDetails.signature}`}
                        alt="Signature"
                        style={{
                          width: "100px",
                          height: "50px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <span>No signature available</span>
                    )}
                  </Label>
                </Col>

                <Col
                  className="mb-1 mt-1"
                  lg="4"
                  md="4"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Label>
                    Company Logo{" "}
                    {companyDetails?.logo ? (
                      <img
                        src={`${baseURL}/Images/${companyDetails.logo}`}
                        alt="Company Logo"
                        style={{
                          width: "100px",
                          height: "50px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <span>No logo available</span>
                    )}
                  </Label>
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={companyDetails.email || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Gst Number *</Label>
                  <Input
                    type="number"
                    name="Gst_No"
                    placeholder="Enter Gst Number"
                    value={companyDetails.gstNo || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Registration Date *</Label>
                  <Input
                    type="date"
                    name="registrationDate"
                    placeholder="Enter Date"
                    value={companyDetails.reDate || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Company Number *</Label>
                  <Input
                    type="number"
                    name="companyNumber"
                    placeholder="Enter Company Number"
                    value={companyDetails.mobileNo || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="8" md="8">
                  <Label>Company Address *</Label>
                  <Input
                    type="textarea"
                    name="addressTwo"
                    placeholder="Address Two"
                    value={Data.address2 || companyDetails.address || ""}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            </CardBody>

            {/* ===== Plan Details ===== */}
            <CardBody className="vs-section userRegiBody col-12 col-md-6 mt-2">
              <h5>Plan Details</h5>
              <Row>
                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label htmlFor="planStatus">Plan Status *</Label>
                  <Input
                    type="select"
                    id="planStatus"
                    name="planStatus"
                    value={Data.planStatus || ""}
                    onChange={handleInputChange}
                  >
                    <option value="Paid">Paid</option>
                    <option value="unPaid">unPaid</option>
                    <option value="Expiry">Expiry</option>
                  </Input>
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label htmlFor="subscriptionPlan">Change Plan</Label>
                  <Input
                    type="select"
                    id="subscriptionPlan"
                    value={Data["subscriptionPlan"] || ""}
                    onChange={(e) =>
                      setData((d) => ({
                        ...d,
                        subscriptionPlan: e.target.value,
                      }))
                    }
                  >
                    <option value="0">----select Plan----</option>
                    {SubsCriptionList?.length > 0 &&
                      SubsCriptionList.map((ele, index) => (
                        <option key={index} value={ele?._id}>
                          {ele?.planName} (Price -{ele?.subscriptionCost})(
                          {ele?.subscriptionType === 1 ? "Yearly" : "One Time"})
                        </option>
                      ))}
                  </Input>
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Plan Name *</Label>
                  <Input
                    type="text"
                    name="planName"
                    placeholder="Plan Name"
                    value={Data.planName || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Plan Price *</Label>
                  <Input
                    type="text"
                    name="subscriptionCost"
                    placeholder="Plan Price"
                    value={Data.subscriptionCost || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Plan StartDate *</Label>
                  <Input
                    type="text"
                    name="planStart"
                    placeholder="Plan Start Date"
                    value={Data.planStart || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Plan EndDate*</Label>
                  <Input
                    type="text"
                    name="planEnd"
                    placeholder="Plan End Date"
                    value={Data.planEnd || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Total User Allowed*</Label>
                  <Input
                    type="number"
                    name="noOfUser"
                    placeholder="No Of User Allowed"
                    value={Data.noOfUser || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Registered User *</Label>
                  <Input
                    type="text"
                    name="PlanPrice"
                    placeholder="Registered User"
                    value={Data.PlanPrice || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Total Days*</Label>
                  <Input
                    type="text"
                    name="days"
                    placeholder="Total Days"
                    value={Data.days || ""}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            </CardBody>

            {/* ===== Bank Details ===== */}
            <CardBody className="vs-section userRegiBody col-12 col-md-5 mt-2">
              <h5>Bank Details</h5>
              <Row>
                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Bank Name *</Label>
                  <Input
                    type="text"
                    name="bankName"
                    placeholder="Bank Name"
                    value={companyDetails.bankName || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Account Number *</Label>
                  <Input
                    type="text"
                    name="accountNumber"
                    placeholder="Account Number"
                    value={companyDetails.accountNumber || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Branch Name *</Label>
                  <Input
                    type="text"
                    name="branchName"
                    placeholder="Branch Name"
                    value={companyDetails.branchName || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>IFSC Code *</Label>
                  <Input
                    type="text"
                    name="ifscCode"
                    placeholder="Ifsc code"
                    value={companyDetails.bankIFSC || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>Bank Address *</Label>
                  <Input
                    type="text"
                    name="bankAddress"
                    placeholder="Bank Address"
                    value={companyDetails.bankAddress || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>MICR Code *</Label>
                  <Input
                    type="text"
                    name="micrCode"
                    placeholder="MICR Code"
                    value={companyDetails.bankMicr || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>UPI Id *</Label>
                  <Input
                    type="text"
                    name="upiID"
                    placeholder="upi id"
                    value={companyDetails.upiId || ""}
                    onChange={handleInputChange}
                  />
                </Col>

                <Col className="mb-1 mt-1" lg="4" md="4">
                  <Label>GPAY/PHONEPE NUMBER*</Label>
                  <Input
                    type="text"
                    name="gpay_phonepenumber"
                    placeholder="Gpay/Phonepe Number"
                    value={companyDetails.gpay_PhonepayNumber || ""}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            </CardBody>
          </div>
        </Form>
      </Card>
    </>
  );
}

export default ViewSuperAdmin;
